import React from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormatAddress from "go-core/components/Formatters/FormatAddress";
import { PrintoutTemplateApi } from "../../../../../../../../../services/Api/Organization/types";
import { PrintoutElement, PrintoutExampleData, PrintoutItem } from "./PrintoutTemplateForm";

interface Props {
	form: UseFormReturn<PrintoutTemplateApi>;
	printoutData: PrintoutExampleData;
}

const PrintoutTemplatePreview = ({ form, printoutData }: Props) => {
	const { watch } = form;
	const { t } = useTranslation();

	const getOrderItems = (item: PrintoutElement) => {
		const mergeItemsValue = watch("merge_items");

		if (mergeItemsValue === "MERGE") {
			const newItems: PrintoutItem[] = [];
			item.items.forEach((x) => {
				const existingItem = newItems.find(
					(newItem) =>
						newItem.name === x.name && JSON.stringify(newItem.additions) === JSON.stringify(x.additions)
				);
				if (existingItem) {
					const existingItemPrice = existingItem.price.split(" ");

					newItems[newItems.indexOf(existingItem)] = {
						...existingItem,
						quantity: existingItem.quantity + x.quantity,
						price: `${Number(existingItemPrice[0]) + x.quantity * Number(x.price.split(" ")[0])} ${
							existingItemPrice[1]
						}`,
					};
				} else {
					newItems.push(x);
				}
			});
			return newItems;
		}
		return item.items;
	};

	const getFontClass = (key: keyof PrintoutTemplateApi) => {
		if (watch(key) === "SMALL") return "small-font";
		if (watch(key) === "LARGE") return "large-font";
		if (watch(key) === "LARGE_DOUBLE_WIDTH") return "large-double-width-font";
		if (watch(key) === "DOUBLE_WIDTH") return "double-width-font";
		return "";
	};

	const isFontLarge = (key: keyof PrintoutTemplateApi) => {
		const fontClassName = getFontClass(key);
		return fontClassName === "large-font" || fontClassName === "large-double-width-font";
	};

	const checkIfItemHasAdditions = (item: PrintoutItem) => {
		return (
			(item.additions && item.additions.length > 0) ||
			(item.additions_separated && item.additions_separated.length > 0) ||
			(item.included_additions && item.included_additions.length > 0)
		);
	};

	return (
		<div className={"col-md-4"}>
			<div className="receipt mb-2">
				<div className="receipt-list">
					<div className="printout-content">
						<div className={"printout-content-section"} />
						<div className={"printout-content-section"}>
							<div className="printout-header">
								<span className={"title"}>{t("modules.printout_template.field.order.title")}</span>
								{watch("print_pickup_at") && (
									<>
										<span className="mb-0">******************************</span>
										<span
											className={"mb-0"}
											style={{
												marginTop: isFontLarge("print_pickup_at_text_size") ? "4px" : "-4px",
											}}
										>
											<div className={"me-auto"}>***</div>
											<div
												className={`dynamic-font-size ${getFontClass(
													"print_pickup_at_text_size"
												)} ${watch("print_pickup_at_bold") ? "bold" : ""}`}
											>
												{t("modules.printout_template.field.realization_date.title")}
											</div>
											<div className={"ms-auto"}>***</div>
										</span>
										<span
											style={{
												marginTop: isFontLarge("print_pickup_at_text_size") ? "4px" : "-4px",
											}}
										>
											<div className={"me-auto"}>***</div>
											<div
												className={`dynamic-font-size ${getFontClass(
													"print_pickup_at_text_size"
												)} ${watch("print_pickup_at_bold") ? "bold" : ""}`}
											>
												2022-04-07 19:35
											</div>
											<div className={"ms-auto"}>***</div>
										</span>
										<span
											className={"mb-0"}
											style={{
												marginTop: isFontLarge("print_pickup_at_text_size") ? "6px" : "-6px",
											}}
										>
											******************************
										</span>
									</>
								)}
								<span style={{ marginTop: watch("print_pickup_at") ? "-4px" : "0" }}>
									<strong>2022-04-07 16:40</strong>
								</span>
							</div>
							{watch("print_employee") && (
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_employee_text_size"
									)} ${watch("print_employee_bold") ? "bold" : ""}`}
								>
									{t("modules.printout_template.field.employee.title")}: {printoutData.employee_name}
								</span>
							)}
							{watch("print_point_of_sale") && (
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_point_of_sale_text_size"
									)} ${watch("print_point_of_sale_bold") ? "bold" : ""}`}
								>
									{`${t("modules.printout_template.field.point_of_sale.title")} "${
										printoutData.point_of_sale
									}"`}
								</span>
							)}
							{watch("print_direction") && (
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_direction_text_size"
									)} ${watch("print_direction_bold") ? "bold" : ""}`}
								>
									{t("modules.printout_template.field.direction.title")}: {printoutData.direction}
								</span>
							)}
							{watch("print_comment") && (
								<span
									className={`printout-item dynamic-font-size comment ${getFontClass(
										"print_comment_text_size"
									)} ${watch("print_comment_bold") ? "bold" : ""}`}
								>
									{t("common.word.comment")}: {printoutData.comment}
								</span>
							)}
							{watch("print_table") && (
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_table_text_size"
									)} ${watch("print_table_bold") ? "bold" : ""}`}
								>
									{t("common.word.table")}: {printoutData.table}
								</span>
							)}
							{watch("print_number_of_guests") && (
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_number_of_guests_text_size"
									)} ${watch("print_number_of_guests_bold") ? "bold" : ""}`}
								>
									{t("modules.printout_template.field.number_of_guests.title")}:{" "}
									{printoutData.guest_amount}
								</span>
							)}
							{watch("print_order_source") && (
								<span
									className={`printout-item dynamic-font-size text-center ${getFontClass(
										"print_order_source_text_size"
									)} text-center ${watch("print_order_source_bold") ? "bold" : ""}`}
								>
									GoOrder.pl #180
								</span>
							)}
							{watch("print_order_type") && (
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_order_type_text_size"
									)} text-center ${watch("print_order_type_bold") ? "bold" : ""}`}
								>
									{t("enums.orders.types.DELIVERY")}
								</span>
							)}
						</div>
						{printoutData.items.map((item) => {
							return (
								<>
									{watch("print_course") && (
										<div className={"printout-content-section"}>
											<span
												className={`printout-item dynamic-font-size comment ${getFontClass(
													"print_course_text_size"
												)} ${watch("print_course_bold") ? "bold" : ""}`}
												style={{ textAlign: "center" }}
											>
												#{item.course}
											</span>
										</div>
									)}
									<div className={"printout-content-section"}>
										{getOrderItems(item).map((subItem, index) => {
											return (
												<React.Fragment key={index}>
													<span className={"printout-item"}>
														<div className={"d-flex"}>
															<span
																className={`printout-item dynamic-font-size me-1 ${getFontClass(
																	"items_text_size"
																)}
																			${watch("items_bold") ? "bold" : ""}
																		`}
															>
																{subItem.name}
																{watch("print_seat") && (
																	<span
																		className={`ms-1 printout-item dynamic-font-size ${getFontClass(
																			"print_seat_text_size"
																		)}
																			${watch("print_seat_bold") ? "bold" : ""}
																		`}
																	>{`(${subItem.seat_name})`}</span>
																)}
															</span>
															{watch("print_prices") && (
																<span
																	className={`dynamic-font-size order-element ms-auto price ${getFontClass(
																		"items_text_size"
																	)} ${watch("items_bold") ? "bold" : ""}`}
																>
																	{subItem.price}
																</span>
															)}
														</div>
														{watch("print_extra_space") &&
															!checkIfItemHasAdditions(subItem) && <br />}
														{watch("print_included_additions") &&
															subItem.included_additions && (
																<div className={"d-flex flex-column"}>
																	{subItem.included_additions.map((add, index) => {
																		return (
																			<span
																				className={`included-addition dynamic-font-size ${getFontClass(
																					"print_included_additions_text_size"
																				)} ${
																					watch(
																						"print_included_additions_bold"
																					)
																						? "bold"
																						: ""
																				}`}
																				key={index}
																			>
																				+ {add.name}
																			</span>
																		);
																	})}
																</div>
															)}
														{watch("additions") && subItem.additions && (
															<div className={"d-flex flex-column"}>
																{subItem.additions.map((addition, additionIndex) => {
																	return (
																		<span
																			className={`included-addition dynamic-font-size ${getFontClass(
																				"additions_text_size"
																			)} ${
																				watch("additions_bold") ? "bold" : ""
																			}`}
																			key={additionIndex}
																		>
																			+ {addition.name}
																		</span>
																	);
																})}
															</div>
														)}
														{subItem.additions_separated && (
															<div className={"d-flex flex-column"}>
																{subItem.additions_separated.map((add, index) => {
																	return (
																		<span
																			className={`included-addition dynamic-font-size text-decoration-line-through ${getFontClass(
																				"additions_text_size"
																			)} ${
																				watch("additions_bold") ? "bold" : ""
																			}`}
																			key={index}
																		>
																			{`[-] ${add.name}`}
																		</span>
																	);
																})}
															</div>
														)}
													</span>
													{watch("print_item_comment") && subItem.item_comment && (
														<span
															className={`printout-item dynamic-font-size ${getFontClass(
																"print_item_comment_text_size"
															)} ${watch("print_item_comment_bold") ? "bold" : ""}`}
														>
															#{t("modules.printout_template.field.item_comment.title")}:{" "}
															{subItem.item_comment}
														</span>
													)}
													{watch("print_extra_space") && checkIfItemHasAdditions(subItem) && (
														<br />
													)}
												</React.Fragment>
											);
										})}
									</div>
								</>
							);
						})}
						<div className={"printout-content-section"}>
							<span className="printout-item dynamic-font-size order-element">
								{t("lib:common.word.source")}: GoCRM
							</span>
							{watch("print_client") && (
								<>
									<span
										className={`printout-item dynamic-font-size ${getFontClass(
											"print_client_text_size"
										)} ${watch("print_client_bold") ? "bold" : ""}`}
									>
										{t("common.word.client")}: {printoutData.client.name}
									</span>
									<span
										className={`printout-item dynamic-font-size ${getFontClass(
											"print_client_text_size"
										)} ${watch("print_client_bold") ? "bold" : ""}`}
									>
										{t("common.word.phone_number", { ns: "lib" })}:{" "}
										{printoutData.client.contact_phone_number}
									</span>
								</>
							)}
						</div>

						{watch("print_address") && (
							<div className={"printout-content-section"}>
								<span
									className={`printout-item dynamic-font-size ${getFontClass(
										"print_address_text_size"
									)} ${watch("print_address_bold") ? "bold" : ""}`}
								>
									{t("common.word.address", { ns: "lib" })}:{" "}
									<FormatAddress address={printoutData.address} />
								</span>
							</div>
						)}
						{watch("print_delivery_employee") && (
							<div className={"printout-content-section"}>
								<span
									className={`printout-item delivery-employee dynamic-font-size ${getFontClass(
										"print_delivery_employee_text_size"
									)} ${watch("print_delivery_employee_bold") ? "bold" : ""}`}
								>
									{t("modules.printout_template.field.delivery_employee.title")}:{" "}
									{printoutData.driver_name}
								</span>
							</div>
						)}
						{(watch("print_payment_method") || watch("print_payment_status")) && (
							<div className={"printout-content-section"}>
								{watch("print_payment_method") && (
									<span
										className={`printout-item dynamic-font-size ${getFontClass(
											"print_payment_method_text_size"
										)} ${watch("print_payment_method_bold") ? "bold" : ""}`}
									>
										{`${t("common.word.payment_method")}: ${t(
											"enums.payment_methods.payment_receipt_types.CASH"
										)}`}
									</span>
								)}
								{watch("print_payment_status") && (
									<span
										className={`dynamic-font-size ${getFontClass(
											"print_payment_status_text_size"
										)} ${watch("print_payment_status_bold") ? "bold" : ""}`}
									>
										{t("modules.printout_template.field.paid.title")}
									</span>
								)}
							</div>
						)}
						{(watch("print_other_directions") || watch("print_direction_if_different")) && (
							<div className="printout-content-section">
								{watch("print_other_directions") && (
									<span
										className={`printout-item dynamic-font-size ${getFontClass(
											"print_other_directions_text_size"
										)} ${watch("print_other_directions_bold") ? "bold" : ""}`}
									>
										{`${t("common.word.other_directions")}: `}
										{printoutData.other_directions.join(", ")}
									</span>
								)}
								{watch("print_direction_if_different") && (
									<span
										className={`printout-item dynamic-font-size ${getFontClass(
											"print_direction_if_different_text_size"
										)} ${watch("print_direction_if_different_bold") ? "bold" : ""}`}
									>
										{`${t("modules.printout_template.field.print_direction_if_different.title")}: ${
											printoutData.direction_if_different
										}`}
									</span>
								)}
							</div>
						)}
						{(watch("print_number") || watch("print_total_price")) && (
							<div className="printout-content-section" style={{ borderBottom: "none" }}>
								{watch("print_number") && (
									<span
										className={`printout-item dynamic-font-size order-element ${getFontClass(
											"print_number_text_size"
										)} ${watch("print_number_bold") ? "bold" : ""}`}
									>
										{t("modules.printout_template.field.order_no.title")}: {printoutData.order_no}
									</span>
								)}
								{watch("print_total_price") && (
									<div className={"d-flex"}>
										<span
											className={`dynamic-font-size ${getFontClass(
												"print_total_price_text_size"
											)} ${watch("print_total_price_bold") ? "bold" : ""}`}
										>
											{t("modules.printout_template.field.summary.title")}:
										</span>
										<span
											className={`ms-auto dynamic-font-size ${getFontClass(
												"print_total_price_text_size"
											)} ${watch("print_total_price_bold") ? "bold" : ""}`}
										>
											{printoutData.price_sum}
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrintoutTemplatePreview;
